import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { ref, get, set, update } from 'firebase/database';
import { database, DB_PATHS } from '../config/firebase';
import dayjs from 'dayjs';
import { Driver } from '../types/models';
import { isChungcheongApp } from '../utils/appNameUtils';

interface ChungcheongExtensionRequestProps {
  open: boolean;
  onClose: () => void;
  selectedDriver: Driver | null;
  currentUser: any;
  onSuccess?: () => void;
}

const ChungcheongExtensionRequest: React.FC<ChungcheongExtensionRequestProps> = ({
  open,
  onClose,
  selectedDriver,
  currentUser,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [extensionAmount, setExtensionAmount] = useState<number>(20000);
  const [managerInfo, setManagerInfo] = useState<any>(null);

  // 현재 관리자 정보 조회
  useEffect(() => {
    const fetchManagerInfo = async () => {
      if (!currentUser?.id) return;

      try {
        const managerRef = ref(database, `${DB_PATHS.MANAGER}/${currentUser.id}`);
        const snapshot = await get(managerRef);
        
        if (snapshot.exists()) {
          const data = snapshot.val();
          setManagerInfo(data);
          // 기본값 설정
          setExtensionAmount(data.extensionAmount || 20000);
        }
      } catch (error) {
        console.error('관리자 정보 조회 실패:', error);
      }
    };

    if (open && currentUser) {
      fetchManagerInfo();
    }
  }, [open, currentUser]);

  // 예치금 확인 (연장금액만 확인)
  const checkManagerDeposit = async (): Promise<boolean> => {
    try {
      if (!currentUser?.id) return false;
      
      const depositRef = ref(database, `deposit/${currentUser.id}`);
      const snapshot = await get(depositRef);
      
      if (snapshot.exists()) {
        const depositData = snapshot.val();
        const currentDeposit = depositData.currentDeposit || 0;
        console.log('현재 관리자 예치금:', currentDeposit);
        console.log('필요한 연장금액:', extensionAmount);
        return currentDeposit >= extensionAmount; // 연장금액만 확인
      } else {
        console.log('예치금 데이터 없음');
        return false;
      }
    } catch (error) {
      console.error('예치금 확인 실패:', error);
      return false;
    }
  };

  const handleExtensionApproval = async () => {
    if (!selectedDriver) return;

    setLoading(true);
    setError(null);

    try {
      // 예치금 확인 (연장금액만 확인)
      const hasEnoughDeposit = await checkManagerDeposit();
      
      if (!hasEnoughDeposit) {
        setError('예치금이 부족합니다. 충전 후 다시 시도해주세요.');
        setLoading(false);
        return;
      }

      // 더 안전한 만료일 계산 로직
      const today = dayjs().startOf('day');
      const currentExpireDate = dayjs(selectedDriver.expiredate).startOf('day');
      
      let baseDate;
      if (today.isAfter(currentExpireDate)) {
        // 만료일이 이미 지난 경우: 오늘 기준으로 1개월 연장
        baseDate = today;
        console.log('만료일이 이미 지남 - 오늘 기준으로 연장');
      } else {
        // 만료일이 아직 유효한 경우: 현재 만료일 기준으로 1개월 연장
        baseDate = currentExpireDate;
        console.log('만료일이 아직 유효함 - 현재 만료일 기준으로 연장');
      }
      
      const newExpireDate = baseDate.add(1, 'month').format('YYYY-MM-DD') + ' 12:00:00';

      // 기사 정보 업데이트
      const driverRef = ref(database, `${DB_PATHS.DRIVERS}/${selectedDriver.id}`);
      await update(driverRef, { expiredate: newExpireDate });

      // 로그 기록
      const now = new Date();
      const timestamp = now.getFullYear().toString() +
        String(now.getMonth() + 1).padStart(2, '0') +
        String(now.getDate()).padStart(2, '0') +
        String(now.getHours()).padStart(2, '0') +
        String(now.getMinutes()).padStart(2, '0') +
        String(now.getSeconds()).padStart(2, '0');

      const logData = {
        driverId: selectedDriver.id,
        driverName: selectedDriver.username,
        oldExpireDate: selectedDriver.expiredate,
        newExpireDate: newExpireDate,
        changedByName: currentUser?.username || 'unknown',
        changedById: currentUser?.id || 'unknown',
        changeDate: now.toISOString(),
        changeType: 'chungcheong_extension_request',
        extensionAmount: extensionAmount,
        totalAmount: extensionAmount
      };

      const logRef = ref(database, `${DB_PATHS.DRIVERLOGS}/${selectedDriver.id}/${timestamp}`);
      await set(logRef, logData);
      const autocalllogRef = ref(database, `${DB_PATHS.AUTOCALLLOGS}/${selectedDriver.id}/${timestamp}`);
      await set(autocalllogRef, logData);

      // 관리자 예치금 차감 처리 (연장금액만 차감)
      if (currentUser?.id) {
        try {
          // 현재 예치금 조회
          const depositRef = ref(database, `deposit/${currentUser.id}`);
          const depositSnapshot = await get(depositRef);
          
          if (depositSnapshot.exists()) {
            const depositData = depositSnapshot.val();
            const currentDeposit = depositData.currentDeposit || 0;
            const newDeposit = currentDeposit - extensionAmount; // 연장금액만 차감

            // deposit 테이블 업데이트
            const depositUpdateData = {
              phonenumber: currentUser.id,
              username: currentUser.username || 'unknown',
              currentDeposit: newDeposit,
              lastUpdateDate: now.getFullYear() + '-' +
                String(now.getMonth() + 1).padStart(2, '0') + '-' +
                String(now.getDate()).padStart(2, '0') + ' ' +
                String(now.getHours()).padStart(2, '0') + ':' +
                String(now.getMinutes()).padStart(2, '0') + ':' +
                String(now.getSeconds()).padStart(2, '0')
            };

            await set(depositRef, depositUpdateData);

            // depositlogs 테이블에 출금 로그 추가
            const depositLogData = {
              type: 'withdraw',
              amount: extensionAmount, // 연장금액만 출금
              date: depositUpdateData.lastUpdateDate,
              reason: `연장요청 승인 - ${selectedDriver.username} (${selectedDriver.phonenumber}) - 연장금액: ${extensionAmount}원`,
              balance: newDeposit
            };

            const depositLogRef = ref(database, `depositlogs/${currentUser.id}/${timestamp}`);
            await set(depositLogRef, depositLogData);

            console.log(`예치금 차감 완료: ${currentDeposit} → ${newDeposit} (연장금액: ${extensionAmount}원)`);
          } else {
            console.error('예치금 데이터를 찾을 수 없습니다.');
          }
        } catch (error) {
          console.error('예치금 차감 처리 실패:', error);
        }
      }

      setLoading(false);
      onSuccess?.();
      onClose();
      alert('연장요청이 승인되었습니다.');
    } catch (error) {
      console.error('연장요청 승인 실패:', error);
      setError('연장요청 승인에 실패했습니다.');
      setLoading(false);
    }
  };

  const handleClose = () => {
    setLoading(false);
    setError(null);
    onClose();
  };

  if (!selectedDriver) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>연장요청 승인</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Typography variant="h6" sx={{ mb: 2 }}>
          <strong>{selectedDriver.username}</strong> 기사의 연장요청
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            현재 만료일: {selectedDriver.expiredate}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            연장 후 만료일: {(() => {
              const today = dayjs().startOf('day');
              const currentExpireDate = dayjs(selectedDriver.expiredate).startOf('day');
              
              if (today.isSame(currentExpireDate) || today.isBefore(currentExpireDate)) {
                return dayjs(selectedDriver.expiredate).add(1, 'month').format('YYYY-MM-DD');
              } else {
                return today.add(1, 'month').format('YYYY-MM-DD');
              }
            })()}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            연장 설정
          </Typography>
          
          <TextField
            label="연장금액"
            type="number"
            value={extensionAmount}
            InputProps={{
              readOnly: true,
            }}
            fullWidth
            margin="dense"
          />
          
          <Typography variant="h6" sx={{ mt: 2, color: 'primary.main' }}>
            연장금액: {extensionAmount.toLocaleString()}원
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          취소
        </Button>
        <Button 
          onClick={handleExtensionApproval} 
          variant="contained" 
          color="primary"
          disabled={loading}
        >
          {loading ? '처리중...' : '승인'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChungcheongExtensionRequest; 